import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../config/firebase';

const BRANDING_BASE = 'businesses';

export type BrandingAssetKind = 'logo' | 'stamp' | 'signature';

const ASSET_FILE_NAMES: Record<BrandingAssetKind, string> = {
  logo: 'logo',
  stamp: 'stamp',
  signature: 'signature',
};

const ALLOWED_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/jpg', 'image/webp']);
export const BRANDING_IMAGE_MAX_BYTES = 500 * 1024;

function getExtension(file: File): string {
  const fromName = file.name.split('.').pop()?.toLowerCase();
  if (fromName && ['png', 'jpg', 'jpeg', 'webp'].includes(fromName)) {
    return fromName === 'jpeg' ? 'jpg' : fromName;
  }
  if (file.type === 'image/png') return 'png';
  if (file.type === 'image/webp') return 'webp';
  return 'jpg';
}

function getBrandingAssetPath(businessId: string, kind: BrandingAssetKind, file: File): string {
  const ext = getExtension(file);
  return `${BRANDING_BASE}/${businessId}/branding/${ASSET_FILE_NAMES[kind]}.${ext}`;
}

function getPathFromDownloadUrl(downloadUrl: string): string | null {
  try {
    const match = downloadUrl.match(/\/o\/(.+?)(\?|$)/);
    if (!match?.[1]) return null;
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

export function validateBrandingImageFile(file: File): string | null {
  if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
    return 'Format accepté : PNG, JPG ou WebP';
  }
  if (file.size > BRANDING_IMAGE_MAX_BYTES) {
    return 'Taille maximale : 500 Ko';
  }
  return null;
}

export async function uploadBusinessBrandingAsset(
  businessId: string,
  kind: BrandingAssetKind,
  file: File
): Promise<string> {
  const validationError = validateBrandingImageFile(file);
  if (validationError) throw new Error(validationError);

  const path = getBrandingAssetPath(businessId, kind, file);
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
}

export async function deleteBusinessBrandingAssetByUrl(url: string): Promise<void> {
  const path = getPathFromDownloadUrl(url);
  if (!path) throw new Error('URL de fichier invalide');
  const storageRef = ref(storage, path);
  await deleteObject(storageRef);
}
